import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { cabinetContractApi, cabinetDictApi, cabinetPersonApi, DictItem } from '../../services/api';
import { ContractD, PersonD } from '../../types';
import dayjs from 'dayjs';
import PageHeader from '../../components/PageHeader';
import MobileEntityCard from '../../components/MobileEntityCard';
import YandexOrdImportPanel from '../../components/YandexOrdImportPanel';
import { useAuth } from '../../contexts/AuthContext';

const MY_SIDE = 'me';

const TYPE_LABELS: Record<string, string> = {
  service: 'Услуги',
  mediation: 'Посреднический',
  additional: 'Доп. соглашение',
  self_promo: 'Самореклама',
};

const SUBJECT_LABELS: Record<string, string> = {
  distribution: 'Распространение',
  org_distribution: 'Орг. распространения',
  mediation: 'Посредничество',
  representation: 'Представительство',
  other: 'Иное',
};

const ContractsPage: React.FC = () => {
  const { user, isOwner, isClientAdmin } = useAuth();
  const isStartTariff = user?.tariff === 'START';
  const canImportPersonsFromOrd =
    !!user
    && user.ordProvider !== 'YANDEX'
    && !isStartTariff
    && (isOwner || isClientAdmin);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [items, setItems] = useState<ContractD[]>([]);
  const [persons, setPersons] = useState<PersonD[]>([]);
  const [contractTypes, setContractTypes] = useState<DictItem[]>([]);
  const [subjectTypes, setSubjectTypes] = useState<DictItem[]>([]);
  const [actionTypes, setActionTypes] = useState<DictItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importingPersons, setImportingPersons] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [serial, setSerial] = useState('');
  const [type, setType] = useState('service');
  const [comment, setComment] = useState('');
  const [clientSide, setClientSide] = useState<string | number>(MY_SIDE);
  const [contractorSide, setContractorSide] = useState<string | number>('');
  /** На «Старте» выбираем только контрагента — стороны выводятся из его роли */
  const [startCounterpartyId, setStartCounterpartyId] = useState<number | ''>('');
  const [subjectType, setSubjectType] = useState('org_distribution');
  const [actionType, setActionType] = useState('distribution');
  const [parentContractDId, setParentContractDId] = useState<number | ''>('');
  const [contractDate, setContractDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [expirationDate, setExpirationDate] = useState('');
  const [amount, setAmount] = useState('');
  const [isCreativeReporter, setIsCreativeReporter] = useState(true);
  const [agentActingForPublisher, setAgentActingForPublisher] = useState(false);
  const [isChargePaidByAgent, setIsChargePaidByAgent] = useState(false);
  const [platformUrls, setPlatformUrls] = useState<string[]>(['']);

  const clientInn = user?.inn?.trim();
  const counterpartyOptions = useMemo(() => {
    const list = isStartTariff && clientInn
      ? persons.filter((p) => !(p.inn && p.inn.trim() === clientInn))
      : persons;
    return list.filter((p) => p.syncedToOrd);
  }, [persons, isStartTariff, clientInn]);

  const selectedCounterparty = useMemo(() => {
    if (isStartTariff) {
      return counterpartyOptions.find((p) => p.personDId === startCounterpartyId) || null;
    }
    const otherId = clientSide === MY_SIDE ? contractorSide : clientSide;
    if (otherId === MY_SIDE || otherId === '' || otherId == null) return null;
    return counterpartyOptions.find((p) => p.personDId === Number(otherId)) || null;
  }, [isStartTariff, startCounterpartyId, counterpartyOptions, clientSide, contractorSide]);

  const counterpartyIsPublisher = useMemo(() => {
    const contractorId = !isStartTariff && contractorSide && contractorSide !== MY_SIDE
      ? Number(contractorSide)
      : null;
    const person = contractorId
      ? counterpartyOptions.find((p) => p.personDId === contractorId)
      : selectedCounterparty;
    const roles = person?.roles || [];
    return roles.includes('publisher');
  }, [isStartTariff, contractorSide, counterpartyOptions, selectedCounterparty]);

  const startCounterparty = useMemo(
    () => counterpartyOptions.find((p) => p.personDId === startCounterpartyId) || null,
    [counterpartyOptions, startCounterpartyId],
  );

  const startSidesHint = useMemo(() => {
    if (!startCounterparty) return null;
    const roles = startCounterparty.roles || [];
    const isAdv = roles.includes('advertiser') && !roles.includes('publisher');
    const isPub = roles.includes('publisher') && !roles.includes('advertiser');
    if (isAdv) {
      return 'Контрагент — рекламодатель → вы исполнитель (издатель), заказчик — контрагент';
    }
    if (isPub) {
      return 'Контрагент — издатель → вы заказчик (рекламодатель), исполнитель — контрагент';
    }
    return 'У контрагента должна быть ровно одна роль (рекламодатель или издатель)';
  }, [startCounterparty]);

  const availableTypes = useMemo(() => {
    if (isStartTariff) {
      return contractTypes.filter((t) => t.code === 'service' || t.code === 'additional');
    }
    return contractTypes;
  }, [contractTypes, isStartTariff]);

  const isMediation = type === 'mediation';
  const isAdditional = type === 'additional';
  const parentOptions = useMemo(
    () => items.filter((c) => c.type !== 'additional' && c.syncedToOrd),
    [items],
  );

  const load = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const [contracts, people, types, subjects, actions] = await Promise.all([
        cabinetContractApi.list(),
        cabinetPersonApi.list().catch(() => [] as PersonD[]),
        cabinetDictApi.contractTypes().catch(() => [] as DictItem[]),
        cabinetDictApi.contractSubjectTypes().catch(() => [] as DictItem[]),
        cabinetDictApi.contractActionTypes().catch(() => [] as DictItem[]),
      ]);
      setItems(contracts);
      setPersons(people);
      setContractTypes(types);
      setSubjectTypes(subjects);
      setActionTypes(actions);
      const options = (isStartTariff && clientInn
        ? people.filter((p) => !(p.inn && p.inn.trim() === clientInn))
        : people).filter((p) => p.syncedToOrd);
      if (isStartTariff) {
        if (!startCounterpartyId && options.length) {
          setStartCounterpartyId(options[0].personDId);
        }
      } else if (!contractorSide && options.length) {
        setContractorSide(options[0].personDId);
      }
      return options.length;
    } catch (e: any) {
      setError(e?.message || 'Ошибка загрузки');
      return 0;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const importPersonsFromOrd = async (opts?: { silent?: boolean }) => {
    if (!canImportPersonsFromOrd) return;
    try {
      setImportingPersons(true);
      if (!opts?.silent) {
        setError(null);
        setSuccess(null);
      }
      const res = await cabinetPersonApi.importFromOrd();
      const n = await load(true);
      const msg = `Контрагенты из ОРД: +${res.personsImported} / обновлено ${res.personsUpdated}`
        + (n ? `. В шторках доступно: ${n}` : '');
      if (res.errors?.length) {
        setError(`${msg}. Ошибки: ${res.errors.slice(0, 3).join('; ')}`);
      } else if (!opts?.silent || res.personsImported > 0 || res.personsUpdated > 0) {
        setSuccess(msg);
      }
    } catch (e: any) {
      if (!opts?.silent) {
        setError(e?.response?.data?.message || e?.message || 'Не удалось подтянуть контрагентов из ОРД');
      }
    } finally {
      setImportingPersons(false);
    }
  };

  useEffect(() => {
    (async () => {
      const n = await load();
      // Если шторки пустые — фоном подтянуть контрагентов из ОРД (Бизнес / владелец)
      if (canImportPersonsFromOrd && n === 0) {
        await importPersonsFromOrd({ silent: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isMediation) {
      setSubjectType('mediation');
      if (!actionType) setActionType('distribution');
    } else if (subjectType === 'mediation') {
      setSubjectType('org_distribution');
    }
  }, [isMediation, actionType, subjectType]);

  const resolveStartSides = (person: PersonD) => {
    const roles = person.roles || [];
    const isPublisher = roles.includes('publisher') && !roles.includes('advertiser');
    const isAdvertiser = roles.includes('advertiser') && !roles.includes('publisher');
    if (!isPublisher && !isAdvertiser) {
      throw new Error(
        'У контрагента должна быть ровно одна роль: рекламодатель или издатель. Обновите контрагента.',
      );
    }
    // publisher → мы заказчик; advertiser → мы исполнитель
    if (isPublisher) {
      return { clientIsMe: true, contractorIsMe: false, otherId: person.personDId };
    }
    return { clientIsMe: false, contractorIsMe: true, otherId: person.personDId };
  };

  const create = async () => {
    let clientIsMe: boolean;
    let contractorIsMe: boolean;
    let clientPersonDId: number | null;
    let contractorPersonDId: number | null;

    if (isStartTariff) {
      if (!startCounterparty) {
        setError('Выберите контрагента');
        return;
      }
      try {
        const sides = resolveStartSides(startCounterparty);
        clientIsMe = sides.clientIsMe;
        contractorIsMe = sides.contractorIsMe;
        clientPersonDId = clientIsMe ? null : sides.otherId;
        contractorPersonDId = contractorIsMe ? null : sides.otherId;
      } catch (e: any) {
        setError(e?.message || 'Некорректная роль контрагента');
        return;
      }
    } else {
      clientIsMe = clientSide === MY_SIDE;
      contractorIsMe = contractorSide === MY_SIDE;
      if (clientIsMe && contractorIsMe) {
        setError('Обе стороны не могут быть вашей организацией');
        return;
      }
      if (!isMediation && clientIsMe === contractorIsMe) {
        setError('Для договора услуг одна сторона — ваша организация, другая — контрагент');
        return;
      }
      if (isMediation && !clientIsMe && !contractorIsMe) {
        // заказчик = РД, исполнитель = блогер — ок
      } else if (!clientIsMe && !clientSide) {
        setError('Выберите заказчика');
        return;
      }
      if (!contractorIsMe && !contractorSide) {
        setError('Выберите исполнителя');
        return;
      }
      if (!clientIsMe && !clientSide) {
        setError('Выберите заказчика');
        return;
      }
      clientPersonDId = clientIsMe ? null : Number(clientSide);
      contractorPersonDId = contractorIsMe ? null : Number(contractorSide);
    }

    if (isAdditional && !parentContractDId) {
      setError('Для доп. соглашения укажите родительский договор');
      return;
    }
    if (isMediation && !actionType) {
      setError('Укажите осуществляемые действия');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await cabinetContractApi.create({
        type,
        serial: serial.trim() || null,
        comment: comment.trim() || null,
        subjectType: isMediation ? 'mediation' : subjectType,
        actionType: isMediation ? actionType : null,
        parentContractDId: isAdditional ? parentContractDId : null,
        contractDate,
        expirationDate: expirationDate || null,
        amount: amount ? Number(amount) : null,
        isCreativeReporter: isStartTariff ? false : isCreativeReporter,
        vatIncluded: true,
        agentActingForPublisher: isMediation ? agentActingForPublisher : false,
        isChargePaidByAgent: isMediation ? isChargePaidByAgent : false,
        clientIsMe,
        clientPersonDId,
        contractorIsMe,
        contractorPersonDId,
        platformUrls: counterpartyIsPublisher
          ? platformUrls.map((u) => u.trim()).filter(Boolean)
          : undefined,
        syncToOrd: true,
      });
      setSuccess('Договор создан и отправлен в ОРД');
      setSerial('');
      setComment('');
      setAmount('');
      setExpirationDate('');
      setParentContractDId('');
      setPlatformUrls(['']);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка создания');
    } finally {
      setSaving(false);
    }
  };

  const canSubmit = isStartTariff
    ? !!contractDate
      && !!type
      && !!subjectType
      && !!startCounterpartyId
      && !!startCounterparty
      && (!isAdditional || !!parentContractDId)
    : !!contractDate
      && !!type
      && !!subjectType
      && !(clientSide === MY_SIDE && contractorSide === MY_SIDE)
      && (isMediation
        ? (clientSide === MY_SIDE || !!clientSide) && (contractorSide === MY_SIDE || !!contractorSide)
          && !(clientSide === MY_SIDE && contractorSide === MY_SIDE)
          && (clientSide !== contractorSide || clientSide === MY_SIDE)
        : (clientSide === MY_SIDE) !== (contractorSide === MY_SIDE)
          && (clientSide === MY_SIDE || !!clientSide)
          && (contractorSide === MY_SIDE || !!contractorSide))
      && (!isAdditional || !!parentContractDId)
      && (!isMediation || !!actionType);

  const renderPartySelect = (
    label: string,
    value: string | number,
    onChange: (v: string | number) => void,
  ) => (
    <FormControl required fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value as string | number)}
      >
        <MenuItem value={MY_SIDE}>Моя организация</MenuItem>
        {counterpartyOptions.map((p) => (
          <MenuItem key={p.personDId} value={p.personDId}>{p.name}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box>
      <PageHeader
        title="Договоры"
        description={
          isStartTariff
            ? 'На «Старте» выберите контрагента — стороны договора подставятся по его роли (РД/РР). Посредничество с Тренди создаётся автоматически.'
            : 'Поля как в ОРД VK / Ozon / Яндекс: тип, стороны, предмет, действия, сроки и флаги отчётности.'
        }
        actions={
          canImportPersonsFromOrd ? (
            <Button
              variant="outlined"
              disabled={importingPersons}
              onClick={() => importPersonsFromOrd()}
            >
              {importingPersons ? 'Тянем…' : 'Контрагенты из ОРД'}
            </Button>
          ) : undefined
        }
      />
      {user?.ordProvider === 'YANDEX' && (
        <YandexOrdImportPanel entityType="contracts" onDone={() => load()} />
      )}
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1.5 }}>{success}</Alert>}
      {importingPersons && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Подтягиваем контрагентов из ОРД для шторки заказчик / исполнитель…
        </Alert>
      )}
      {!loading && counterpartyOptions.length === 0 && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {isStartTariff
            ? 'Нет контрагентов в шторке — создайте их на вкладке «Контрагенты» и дождитесь синхронизации с ОРД.'
            : canImportPersonsFromOrd
              ? 'Шторка пустая — нажмите «Контрагенты из ОРД» или заведите контрагента вручную.'
              : 'Нет контрагентов для выбора сторон договора.'}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          Создание договора
        </Typography>
        <Stack spacing={1.5}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <TextField
              label="Серийный номер"
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              fullWidth
              placeholder="Без № и слова «оферта»"
            />
            <FormControl required fullWidth>
              <InputLabel>Тип *</InputLabel>
              <Select
                label="Тип *"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                {availableTypes.map((t) => (
                  <MenuItem key={t.code} value={t.code}>{t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <TextField
            label="Комментарий к договору"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            fullWidth
            placeholder="Краткое описание (Ozon)"
          />

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            {isStartTariff ? (
              <FormControl required fullWidth>
                <InputLabel>Контрагент *</InputLabel>
                <Select
                  label="Контрагент *"
                  value={startCounterpartyId}
                  onChange={(e) => setStartCounterpartyId(e.target.value as number)}
                >
                  {counterpartyOptions.map((p) => (
                    <MenuItem key={p.personDId} value={p.personDId}>
                      {p.name}
                      {p.roles?.length
                        ? ` (${p.roles.map((r) => (r === 'advertiser' ? 'РД' : r === 'publisher' ? 'РР' : r)).join(', ')})`
                        : ''}
                    </MenuItem>
                  ))}
                </Select>
                {startSidesHint && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                    {startSidesHint}
                  </Typography>
                )}
              </FormControl>
            ) : (
              <>
                {isMediation && (
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    Посредничество агентства: заказчик = рекламодатель, исполнитель = блогер
                    (ваша организация — посредник). Один серийный номер можно указать у нескольких записей
                    (разные блогеры). В списках будет «номер — блогер» (только у нас, в ОРД уходит номер как есть).
                  </Alert>
                )}
                {renderPartySelect('Заказчик *', clientSide, (v) => {
                  setClientSide(v);
                  if (!isMediation && v !== MY_SIDE && contractorSide !== MY_SIDE) {
                    setContractorSide(MY_SIDE);
                  }
                })}
                {renderPartySelect('Исполнитель *', contractorSide || '', (v) => {
                  setContractorSide(v);
                  if (!isMediation && v !== MY_SIDE && clientSide !== MY_SIDE) {
                    setClientSide(MY_SIDE);
                  }
                })}
              </>
            )}
          </Stack>

          {counterpartyIsPublisher && (
            <Stack spacing={1}>
              <Alert severity="info" sx={{ py: 0.5 }}>
                Исполнитель — издатель/блогер: укажите ссылки на каналы. Создадим площадки на него
                (название «Имя — YouTube/VK…»). Для чистого рекламодателя ссылки не нужны.
              </Alert>
              {platformUrls.map((url, idx) => (
                <Stack key={idx} direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    label={`Ссылка на канал ${idx + 1}`}
                    value={url}
                    onChange={(e) => {
                      const next = [...platformUrls];
                      next[idx] = e.target.value;
                      setPlatformUrls(next);
                    }}
                    fullWidth
                    placeholder="https://www.youtube.com/@channel"
                  />
                  {platformUrls.length > 1 && (
                    <Button
                      color="inherit"
                      onClick={() => setPlatformUrls(platformUrls.filter((_, i) => i !== idx))}
                    >
                      Убрать
                    </Button>
                  )}
                </Stack>
              ))}
              <Button size="small" onClick={() => setPlatformUrls([...platformUrls, ''])} sx={{ alignSelf: 'flex-start' }}>
                + ещё ссылку
              </Button>
            </Stack>
          )}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <FormControl required fullWidth disabled={isMediation}>
              <InputLabel>Предмет договора *</InputLabel>
              <Select
                label="Предмет договора *"
                value={isMediation ? 'mediation' : subjectType}
                onChange={(e) => setSubjectType(e.target.value)}
              >
                {subjectTypes.map((s) => (
                  <MenuItem key={s.code} value={s.code}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {isMediation && (
              <FormControl required fullWidth>
                <InputLabel>Осуществляемые действия *</InputLabel>
                <Select
                  label="Осуществляемые действия *"
                  value={actionType}
                  onChange={(e) => setActionType(e.target.value)}
                >
                  {actionTypes.map((a) => (
                    <MenuItem key={a.code} value={a.code}>{a.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>

          {isAdditional && (
            <FormControl required fullWidth>
              <InputLabel>Родительский договор *</InputLabel>
              <Select
                label="Родительский договор *"
                value={parentContractDId}
                onChange={(e) => setParentContractDId(e.target.value as number)}
              >
                {parentOptions.map((c) => (
                  <MenuItem key={c.contractDId} value={c.contractDId}>
                    {c.serial || c.externalId} · {c.counterpartyName || '—'} · {c.contractDate}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <TextField
              required
              label="Дата заключения *"
              type="date"
              value={contractDate}
              onChange={(e) => setContractDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Дата окончания (если есть)"
              type="date"
              value={expirationDate}
              onChange={(e) => setExpirationDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Сумма, ₽ (включая все налоги)"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              fullWidth
              placeholder="Введите сумму"
            />
          </Stack>

          <Stack spacing={0.5}>
            {!isStartTariff && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isCreativeReporter}
                    onChange={(e) => setIsCreativeReporter(e.target.checked)}
                  />
                }
                label="Подрядчик обязуется вести учёт креативов"
              />
            )}
            {isMediation && (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={agentActingForPublisher}
                      onChange={(e) => setAgentActingForPublisher(e.target.checked)}
                    />
                  }
                  label="Посредник действует в интересах рекламораспространителя"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isChargePaidByAgent}
                      onChange={(e) => setIsChargePaidByAgent(e.target.checked)}
                    />
                  }
                  label="Сбор 3% платит посредник"
                />
              </>
            )}
          </Stack>

          <Box>
            <Button
              variant="contained"
              disabled={saving || !canSubmit}
              onClick={create}
            >
              {saving ? <CircularProgress size={20} /> : 'Создать и отправить в ОРД'}
            </Button>
          </Box>
        </Stack>
      </Paper>

      {loading ? <CircularProgress /> : isMobile ? (
        <Stack spacing={1.25}>
          {items.map((c) => (
            <MobileEntityCard
              key={c.contractDId}
              title={c.displayLabel || c.counterpartyName || `Договор #${c.contractDId}`}
              subtitle={`#${c.contractDId} · ${c.contractDate || '—'}${c.clientPersonName ? ` · РД: ${c.clientPersonName}` : ''}`}
              chips={
                <>
                  <Chip size="small" label={TYPE_LABELS[c.type] || c.type} />
                  {c.clientPersonDId && c.contractorPersonDId ? (
                    <Chip size="small" label="РД + блогер" />
                  ) : (
                    <Chip size="small" label={c.myRole === 'CONTRACTOR' ? 'я исполнитель' : 'я заказчик'} />
                  )}
                  <Chip size="small" color={c.syncedToOrd ? 'success' : 'default'} label={c.syncedToOrd ? 'в ОРД' : 'локально'} />
                </>
              }
              meta={[
                { label: 'Номер', value: c.serial || '—' },
                { label: 'Заказчик', value: c.clientPersonName || (c.myRole === 'CLIENT' ? 'мы' : '—') },
                { label: 'Исполнитель', value: c.contractorPersonName || (c.myRole === 'CONTRACTOR' ? 'мы' : c.counterpartyName) || '—' },
                { label: 'Сумма', value: c.amount ?? '—' },
              ]}
            />
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Тип</TableCell>
                <TableCell>Договор</TableCell>
                <TableCell>Заказчик</TableCell>
                <TableCell>Исполнитель</TableCell>
                <TableCell>Дата</TableCell>
                <TableCell>Сумма</TableCell>
                <TableCell>ОРД</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.contractDId}>
                  <TableCell>{c.contractDId}</TableCell>
                  <TableCell>{TYPE_LABELS[c.type] || c.type}</TableCell>
                  <TableCell>{c.displayLabel || c.serial || '—'}</TableCell>
                  <TableCell>{c.clientPersonName || (c.myRole === 'CLIENT' ? 'мы' : '—')}</TableCell>
                  <TableCell>{c.contractorPersonName || (c.myRole === 'CONTRACTOR' ? 'мы' : c.counterpartyName) || '—'}</TableCell>
                  <TableCell>{c.contractDate}</TableCell>
                  <TableCell>{c.amount ?? '—'}</TableCell>
                  <TableCell>{c.syncedToOrd ? 'да' : 'нет'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ContractsPage;
